import { motion } from "framer-motion";
import { MaskedReveal } from "@/components/MaskedReveal";

const IMG = {
  coffee:
    "https://images.unsplash.com/photo-1495862433577-132cf20d7902?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxzcGVjaWFsdHklMjBjb2ZmZWUlMjBwb3VyfGVufDB8fHx8MTc4Mzk1OTE0NHww&ixlib=rb-4.1.0&q=85",
  meeting:
    "https://images.unsplash.com/photo-1571624436279-b272aff752b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbWVldGluZyUyMHJvb218ZW58MHx8fHwxNzgzOTU5MTQ0fDA&ixlib=rb-4.1.0&q=85",
  lounge:
    "https://customer-assets.emergentagent.com/job_cowork-points/artifacts/zfm3qoqg_image.png",
};

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-10% 0px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

const ImageCell = ({ src, kicker, title, body, ratio, testid }) => (
  <motion.div {...reveal} data-testid={testid} className="group flex flex-col">
    <div className={`relative overflow-hidden ${ratio}`}>
      <img
        src={src}
        alt={title}
        className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
        loading="lazy"
      />
      <div className="pointer-events-none absolute inset-0 bg-espresso/10" />
    </div>
    <span className="mt-6 font-body text-xs font-semibold uppercase tracking-[0.24em] text-terracotta">
      {kicker}
    </span>
    <h3 className="mt-2 font-display text-3xl font-light tracking-tight text-espresso">{title}</h3>
    <p className="mt-3 max-w-md font-body text-base font-light leading-relaxed text-stone">{body}</p>
  </motion.div>
);

const TextCell = ({ kicker, title, body, testid }) => (
  <motion.div
    {...reveal}
    data-testid={testid}
    className="flex flex-col justify-between border border-espresso/12 bg-sand p-8 sm:p-10"
  >
    <span className="font-body text-xs font-semibold uppercase tracking-[0.24em] text-terracotta">
      {kicker}
    </span>
    <div className="mt-16">
      <h3 className="font-display text-4xl font-light leading-none tracking-tight text-espresso sm:text-5xl">
        {title}
      </h3>
      <p className="mt-4 max-w-md font-body text-base font-light leading-relaxed text-stone">{body}</p>
    </div>
  </motion.div>
);

export const Features = () => {
  return (
    <section id="features" className="px-6 py-24 sm:px-10 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-20 flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">
              The space
            </span>
            <MaskedReveal
              as="h2"
              className="mt-6 font-display font-light tracking-tight text-espresso"
              lineClassName="text-4xl leading-[1.02] sm:text-5xl"
              lines={["Everything a", "good day's work asks for."]}
            />
          </div>
          <p className="max-w-sm font-body text-base font-light leading-relaxed text-stone">
            A modern, calm floor in Johor — designed for deep focus, easy meetings
            and unhurried conversation over exceptional coffee.
          </p>
        </div>

        {/* Bento grid — asymmetric */}
        <div className="grid gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <ImageCell
              testid="feature-coffee"
              src={IMG.coffee}
              ratio="aspect-[16/10]"
              kicker="Café · by The 18 Coffee Roasters"
              title="Specialty coffee, in-house"
              body={<>An exclusive members' café pouring beans roasted by <strong className="font-semibold text-espresso">The 18 Coffee Roasters</strong> — the kind of cup that makes the walk up worth it.</>}
            />
          </div>
          <div className="md:col-span-5">
            <TextCell
              testid="feature-desks"
              kicker="Desks"
              title="On-demand & long-term"
              body="Drop in for an hour of hot-desking or settle into a dedicated spot for the season. Spend points only for the time you claim."
            />
          </div>
          <div className="md:col-span-5">
            <ImageCell
              testid="feature-meeting"
              src={IMG.meeting}
              ratio="aspect-[4/5]"
              kicker="Meeting rooms"
              title="Rooms, by the hour"
              body="Quiet, considered spaces for pitches and stand-ups. Book with points, only when the calendar calls for it."
            />
          </div>
          <div className="md:col-span-7">
            <ImageCell
              testid="feature-lounge"
              src={IMG.lounge}
              ratio="aspect-[16/10]"
              kicker="Lounge & events"
              title="A lounge that hosts"
              body="Unwind between sessions, or host an event for fellow members and earn points back. Community is the point — literally."
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
